import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, MessageCircle, ArrowLeft, Send, CheckCircle2, Globe2, FileQuestion } from "lucide-react";

export default function Support() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) { toast.error(t("support.msg_too_short")); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("support.sent_toast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("support.send_error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="support-page">
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" /> {t("nav.home")}
      </Link>

      <div className="mec-overline mb-3 inline-flex items-center gap-1.5">
        <MessageCircle className="w-3 h-3" /> {t("support.overline")}
      </div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
        {t("support.title")}
      </h1>
      <p className="text-slate-600 mb-10 max-w-2xl">
        {t("support.subtitle")}
      </p>

      <div className="grid lg:grid-cols-3 gap-5 mb-10">
        <div className="mec-card p-6 mec-card-hover">
          <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center mb-3">
            <Mail className="w-5 h-5 text-[#004EEB]" />
          </div>
          <div className="font-display font-semibold text-slate-900 mb-1">Email</div>
          <p className="text-sm text-slate-600 mb-3">{t("support.email_desc")}</p>
          <a href="mailto:contact@k2mondemploiconnect.com" className="text-sm font-mono text-[#004EEB] hover:underline break-all">
            contact@k2mondemploiconnect.com
          </a>
        </div>

        <div className="mec-card p-6 mec-card-hover">
          <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center mb-3">
            <FileQuestion className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="font-display font-semibold text-slate-900 mb-1">FAQ</div>
          <p className="text-sm text-slate-600 mb-3">{t("support.faq_desc")}</p>
          <span className="text-sm text-slate-400">{t("support.coming_soon")}</span>
        </div>

        <div className="mec-card p-6 mec-card-hover">
          <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center mb-3">
            <Globe2 className="w-5 h-5 text-amber-600" />
          </div>
          <div className="font-display font-semibold text-slate-900 mb-1">{t("support.langs_title")}</div>
          <p className="text-sm text-slate-600">FR · EN · ES · DE · IT · PT</p>
        </div>
      </div>

      <div className="mec-card p-8">
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">{t("support.form_title")}</h2>
        <p className="text-sm text-slate-600 mb-6">{t("support.form_subtitle")}</p>

        {sent ? (
          <div className="text-center py-12" data-testid="support-sent">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{t("support.sent_title")}</h3>
            <p className="text-slate-600 mb-6">
              {t("support.sent_desc", { name: form.name })}<br />
              {t("support.sent_reply")} <strong>{form.email}</strong>.
            </p>
            <Button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", category: "general", message: "" }); }} variant="outline">
              {t("support.send_another")}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t("support.name")} *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <Label>{t("support.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="contact-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t("support.cat_general")}</SelectItem>
                    <SelectItem value="support">{t("support.cat_support")}</SelectItem>
                    <SelectItem value="billing">{t("support.cat_billing")}</SelectItem>
                    <SelectItem value="partnership">{t("support.cat_partnership")}</SelectItem>
                    <SelectItem value="press">{t("support.cat_press")}</SelectItem>
                    <SelectItem value="other">{t("support.cat_other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>{t("support.subject")} *</Label>
                <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={t("support.subject_placeholder")} data-testid="contact-subject" />
              </div>
            </div>
            <div>
              <Label>{t("support.message")} *</Label>
              <Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" placeholder={t("support.message_placeholder")} />
            </div>
            <p className="text-xs text-slate-500">
              {t("support.consent_prefix")} <Link to="/privacy" className="text-[#004EEB] hover:underline">{t("support.privacy_link")}</Link>.
            </p>
            <Button type="submit" disabled={loading} className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="contact-submit">
              <Send className="w-4 h-4 mr-1.5" /> {loading ? t("support.sending") : t("support.send_button")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
